import { useEffect, useRef, useState } from "react";
import type { CardType, Launch, Stage, Template } from "../../types/domain";
import {
  IcoClock,
  IcoCopy,
  IcoDragGrip,
  IcoEdit,
  IcoMore,
  IcoPlus,
  IcoSparkle,
  IcoTrash,
} from "../../components/icons";
import { ApplyPopover } from "./ApplyPopover";
import { useCanWrite } from "../../hooks/useCanWrite";

const STAGE_META: Record<Stage, { label: string; color: string; bg: string; text: string }> = {
  warmup: { label: "Прогрев", color: "#3B82F6", bg: "#DBEAFE", text: "#1E40AF" },
  sales: { label: "Продажи", color: "#14B8A6", bg: "#CCFBF1", text: "#0D6B63" },
  close: { label: "Дожим", color: "#F59E0B", bg: "#FEF3C7", text: "#92540C" },
};

interface Props {
  template: Template;
  type?: CardType;
  launches: Launch[];
  activeLaunchId: string | null;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export function TemplateRow({
  template,
  type,
  launches,
  activeLaunchId,
  onEdit,
  onDuplicate,
  onDelete,
}: Props) {
  const canWrite = useCanWrite();
  const [applyOpen, setApplyOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const stage = STAGE_META[template.stage];
  const tags = template.tags ?? [];
  const visibleTags = tags.slice(0, 3);
  const extraTags = Math.max(0, tags.length - visibleTags.length);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    try {
      e.dataTransfer.setData("application/x-launchlab-template", template.id);
      e.dataTransfer.effectAllowed = "copy";
    } catch {
      // ignore
    }
    document.body.classList.add("tpl-dragging");
  };

  const handleDragEnd = () => {
    document.body.classList.remove("tpl-dragging");
  };

  return (
    <div
      className={"templates-row" + (applyOpen || menuOpen ? " templates-row-open" : "")}
      onClick={onEdit}
      draggable={canWrite}
      onDragStart={canWrite ? handleDragStart : undefined}
      onDragEnd={canWrite ? handleDragEnd : undefined}
    >
      {canWrite && (
        <div className="templates-row-drag" title="Перетащите на день доски">
          <IcoDragGrip size={14} />
        </div>
      )}

      <div className="templates-row-main">
        <div className="templates-row-title-line">
          {type && (
            <span
              className="type-badge templates-type-badge"
              style={
                {
                  "--type-color": type.color,
                  "--type-bg": type.bg,
                  "--type-text": type.text,
                } as React.CSSProperties
              }
            >
              <span className="type-dot" />
              {type.name}
            </span>
          )}
          <span className="templates-row-name">{template.name}</span>
          {template.time && (
            <span className="card-time templates-row-time">
              <IcoClock size={10} /> {template.time}
            </span>
          )}
          {template.source === "preset" && (
            <span className="templates-row-preset" title="Встроенный шаблон">
              <IcoSparkle size={10} /> preset
            </span>
          )}
        </div>
        <div className="templates-row-subject">
          {template.subject || (
            <span style={{ color: "var(--text-muted)" }}>Без темы</span>
          )}
        </div>
      </div>

      <div>
        <span className="templates-stage-pill" style={{ color: stage.text, background: stage.bg }}>
          <span className="stage-dot" style={{ background: stage.color }} />
          {stage.label}
        </span>
      </div>

      <div className="templates-row-tags">
        {visibleTags.map((t) => (
          <span key={t} className="templates-tag">
            #{t}
          </span>
        ))}
        {extraTags > 0 && <span className="templates-tag templates-tag-more">+{extraTags}</span>}
      </div>

      <div className="templates-row-usage">
        <b>{template.usageCount ?? 0}</b>
        <span>
          {template.lastUsedAt
            ? `посл. ${new Date(template.lastUsedAt).toLocaleDateString("ru")}`
            : "не применялся"}
        </span>
      </div>

      <div className="templates-row-actions" onClick={(e) => e.stopPropagation()}>
        {canWrite && (
          <div className="templates-apply-wrap" style={{ position: "relative" }}>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setApplyOpen((v) => !v)}
            >
              <IcoPlus size={12} /> Применить
            </button>
            {applyOpen && (
              <ApplyPopover
                template={template}
                launches={launches}
                defaultLaunchId={activeLaunchId}
                onClose={() => setApplyOpen(false)}
                onApplied={() => setApplyOpen(false)}
              />
            )}
          </div>
        )}
        <div className="templates-menu-wrap" ref={menuRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="launch-card-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            title="Ещё"
            aria-label="Действия"
          >
            <IcoMore size={16} />
          </button>
          {menuOpen && (
            <div className="col-dropdown launch-card-dropdown">
              <button
                type="button"
                className="col-dropdown-item"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                <IcoEdit size={13} /> {canWrite ? "Редактировать" : "Подробнее"}
              </button>
              {canWrite && (
                <>
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onDuplicate();
                    }}
                  >
                    <IcoCopy size={13} /> Дублировать
                  </button>
                  {template.source !== "preset" && (
                    <>
                      <div className="col-dropdown-sep" />
                      <button
                        type="button"
                        className="col-dropdown-item col-dropdown-danger"
                        onClick={() => {
                          setMenuOpen(false);
                          onDelete();
                        }}
                      >
                        <IcoTrash size={13} /> Удалить
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
